//#region Imports and Global Definitions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, View, SafeAreaView, Platform, useWindowDimensions, FlatList } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import { ActivityIndicator, Card } from 'react-native-paper';
import { createCommonStyles } from '../../../styles/commonStyles';
import { showMessage } from "react-native-flash-message";
import { useTableHelper } from '../../../hooks/useTableHelper';
import { useTranslation } from 'react-i18next';
import { useTraining } from '../../../hooks/useTraining';
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
//#endregion

const CategoriesListView = React.memo(({ item, index, styles, handlePress,
    handleEditPress, handleDeleteCategory, insertData, hasChildren
}) => {

    return (
        <SafeAreaView>
            <SafeAreaView style={styles.row}>
                <View style={[styles.item, { justifyContent: 'flex-start' }]}>
                    <CustomInput
                        value={item.category}
                        setValue={(e) => { handleEditPress(index, item, 'category', e) }}
                        maxLen={10}
                        type="MAININPUT"
                    />
                </View>
                <View style={[styles.item]}>
                    <CustomInput
                        value={item.description}
                        setValue={(e) => { handleEditPress(index, item, 'description', e) }}
                        maxLen={50}
                        type="MAININPUT"
                    />
                </View>
                {!hasChildren[item.category] &&
                    <CustomButton
                        iconName='trash-outline'
                        onPress={() => {
                            handleDeleteCategory(item, index, 'rowguid')
                        }}
                        show='HOME_BELL'
                        type='HOME_BELL'
                        textTop='2'
                        sz={30} />
                }
                <CustomButton
                    iconName='ellipsis-vertical-outline'
                    onPress={() => { handlePress(item, index) }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30}
                />
            </SafeAreaView>
        </SafeAreaView>
    );
});

const HRAdminCategoriesScreen = (props) => {
    //#region Props
    const { setShowSubjectLines, showSubjectLines, pressedOnSubject,
        selectedCategory, setSelectedCategory } = props;
    //#endregion

    //#region Const Definitions
    const ref = useRef(null);
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const [isLoading, setIsLoading] = useState(true);

    const { loadCategories, updateCategories, insertCategories, deleteCategories, loadSubjects } = useTraining();
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const { rowEdit, rowDelete, rowAdd, handleCancelChanges, hasDuplicates } = useTableHelper();

    const [categoryData, setCategoryData] = useState([]);
    const [subjectData, setSubjectData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [changes, setChanges] = useState({});
    //const [selectedCategory, setSelectedCategory] = useState(null);
    const [insertData, setInsertData] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [hasChildren, setHasChildren] = useState([]);
    const [buttonStates, setButtonStates] = useState({ pressed: {} })
    //#endregion

    useEffect(() => {
        setIsLoading(true);
        // Load categories
        Promise.all([loadCategories(), loadSubjects()])
            .then(([categories, subjects]) => {
                setChanges({});
                setCategoryData(categories);
                setOriginalData([...categories]);
                setSubjectData(subjects);

                // Create a lookup object in the form: { categoryName: true/false }
                const hasChildrenLookup = {};
                categories.forEach(cat => {
                    hasChildrenLookup[cat.category] = subjects.some(subj => subj.category === cat.category);
                });

                setHasChildren(hasChildrenLookup);
                setRefreshAfterSave(false);
                setIsLoading(false);
            })
            .catch(() => {
                setRefreshAfterSave(false);
                setIsLoading(false);
            });
        setRefreshAfterSave(false);
        setIsLoading(false);
    }, [refreshAfterSave])

    //#region Components
    const renderData = (item, index) => {
        return (
            <CategoriesListView item={item} index={index}
                styles={styles}
                handleEditPress={handleEditPress}
                handleDeleteCategory={handleDeleteCategory}
                insertData={insertData}
                hasChildren={hasChildren}
                handlePress={handlePress}
            />
        )
    }

    const renderFunctions = useCallback((item, index) => {
        return (
            <Card
                style={[styles.cardStyleFunctions]} >
                <Card.Content>
                    {renderData(item, index)}
                </Card.Content>
            </Card >
        )
    }, [categoryData]);
    //#endregion

    //#region Handlers
    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, categoryData, setCategoryData)
    }, []);

    const handleDeleteCategory = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, categoryData, setCategoryData, setDeletedItems, ref)
    }

    const handleAddNewCategory = () => {
        let temp = [...categoryData];
        if (categoryData.length === 0)
            temp = {
                category: "",
                description: "",
                rowguid: generateUniqueID()
            }
        rowAdd(temp, setCategoryData, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertData, setInsertData, id = 'rowguid',);
    }

    const removeFromChangesAfterSave = () => {
        setChanges([])
        setDeletedItems([])
    }

    const validateInput = () => {
        let ret = [true, t('common:validationSuccess')]
        categoryData.forEach((itr) => {
            if (itr.category === '')
                ret = [false, t('common:mandatoryCodeNotGivenCategory')];
            if (itr.description === '')
                ret = [false, t('common:descEmpty')]

        })
        return (ret);
    }

    const handleSave = async () => {
        let newPromises = [];
        let deleteSucc = true;
        let insertSucc = true;
        let updateSucc = true;

        if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
            showMessage({
                message: t('common:noChanges'),
                type: "warning",
                duration: 3000,
            })
            return;
        }

        let valid = true;
        const attributesToIgnore = ['description'];

        if (categoryData.length > 0)
            Object.keys(categoryData[0]).forEach(key => {
                // Ignore Keys that we know can be duplicated 
                if (!attributesToIgnore.includes(key)) {
                    if (hasDuplicates(categoryData, key, setDuplicates)) {
                        showMessage({
                            message: t('common:duplicates'),
                            type: "danger",
                            duration: 3000,
                        });
                        valid = false;
                    }
                }
            });

        if (!valid) return null;

        let valRet = validateInput();
        if (!valRet[0]) {
            showMessage({
                message: valRet[1],
                type: "danger",
                duration: 3000,
            })
            return;
        }
        setIsLoading(true);
        let dataForInsert = [];
        let dataForUpdate = [];

        Object.entries(categoryData).forEach(([key, obj]) => {
            if (insertData.includes(obj.rowguid)) {
                dataForInsert.push(obj);
            } else {
                dataForUpdate.push(obj);
            }
        });

        if (Object.keys(dataForUpdate).length > 0) {
            newPromises.push(updateCategories(dataForUpdate)
                .then((res) => {
                    if (res !== 200) {
                        updateSucc = false;
                    } else {
                        removeFromChangesAfterSave()
                    }
                })
                .catch(() => { }));
        }

        if (Object.keys(dataForInsert).length > 0) {
            newPromises.push(insertCategories(dataForInsert)
                .then((res) => {
                    if (res !== 200) insertSucc = false;
                    removeFromChangesAfterSave()
                })
                .catch(() => { }));
        }

        if (deletedItems.length > 0) {
            newPromises.push(deleteCategories(deletedItems).then((res) => {
                if (res !== 200) deleteSucc = false;
                removeFromChangesAfterSave()
            })
                .catch(() => { }));
        }

        try {
            await Promise.all(newPromises).then(() => {
                setIsLoading(false);
                if (!insertSucc || !deleteSucc || !updateSucc) {
                    if (!deleteSucc) {
                        showMessage({
                            message: t('common:issueMessage'),
                            type: "danger",
                            duration: 3000,
                        });
                        let removeDeleteBranches = Object.fromEntries(Object.entries(changes).filter(([key, value]) =>
                            value.isDeleted !== true)
                        )
                        setChanges(removeDeleteBranches);
                    } else {
                        showMessage({
                            message: t('common:issueMessage'),
                            type: "danger",
                            duration: 3000,
                        })
                        setDuplicates([]);
                        setRefreshAfterSave(true);
                    }
                } else if (insertSucc && deleteSucc && updateSucc) {
                    showMessage({
                        message: t('common:allOperationsSuccMess'),
                        type: "success",
                        duration: 3000,
                    });
                    setDuplicates([]);
                    setRefreshAfterSave(true);
                }
            });
        } catch (error) {
            setRefreshAfterSave(true);
            setDuplicates([]);
            setIsLoading(false);
        }
    }

    //#region Press Handle
    const handlePress = (item, index) => {
        if (!showSubjectLines) {
            pressedOnSubject();
        }
        setSelectedCategory(item.category);
        if (selectedCategory === item.category && showSubjectLines)
            return;
        setButtonStates((prevState) => {
            const newPressedState = Object.keys(prevState.pressed).reduce((acc, key) => {
                acc[key] = false;
                return acc;
            }, {});
            newPressedState[index] = !prevState.pressed[index];
            return {
                ...prevState,
                pressed: newPressedState,
            };
        });
    }

    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <SafeAreaView style={styles.rowView}>
                <SafeAreaView style={[styles.subMain]}>
                    <View style={styles.innerView}>
                        {
                            <View style={[styles.buttons, styles.functionalityButtons]}>
                                <CustomButton
                                    text={t('common:new')}
                                    onPress={() => { handleAddNewCategory() }}
                                    type='BUTTON_ADD'
                                    textTop='0'
                                    sz={15}
                                />
                                <CustomButton
                                    text={t('common:save')}
                                    onPress={() => { handleSave() }}
                                    type='BUTTON_SAVE'
                                    textTop='0'
                                    sz={15}
                                    isGradient={true}
                                />
                                <CustomButton
                                    text={t('common:cancel')}
                                    onPress={() => {
                                        handleCancelChanges(setCategoryData, originalData, setDeletedItems, setChanges);
                                    }}
                                    type="BUTTON_CANCEL"
                                    textTop='0'
                                    sz={15}
                                />
                            </View>
                        }
                        <View style={styles.functionsView}>
                            <FlatList
                                style={styles.fullWidth}
                                data={categoryData}
                                keyExtractor={(item, index) => `${item.category}-${index}`}
                                renderItem={({ item, index }) =>
                                    renderFunctions(item, index)
                                }
                                scrollEnabled={true}
                                refreshing={isLoading}
                                ref={ref}
                                ListHeaderComponent={
                                    <SafeAreaView>
                                        <View style={[styles.row, { marginRight: '3%', }]}>
                                            <CustomLabel
                                                value={t('common:category')}
                                                type='SUBTITLE' />
                                            <CustomLabel
                                                value={t('common:description')}
                                                type='SUBTITLE' />
                                        </View>
                                        <SafeAreaView style={common.divider} />
                                    </SafeAreaView>
                                }
                                ListEmptyComponent={
                                    isLoading ?
                                        <ActivityIndicator animating={true} color={colors.blue} />
                                        :
                                        <CustomLabel
                                            value={t("common:functionsNoDataPreview")}
                                            type="NO_FUNCTIONS"
                                        />
                                }
                                initialNumToRender={4}
                            />
                        </View>
                    </View>
                </SafeAreaView>
            </SafeAreaView>
        </SafeAreaView>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardStyleFunctions: {
        margin: 10,
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    selectedButton: {
        margin: 10,
        borderRadius: 5,
        borderWidth: 2,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        borderColor: colors.buttons_submit,
    },
    backButton: {
        alignSelf: 'flex-end',
        margin: 0
    },
    innerView: {
        flex: 1,
    },
    functionsView: {
        flexDirection: 'row',
        height: '100%'
    },
    fullHeight: {
        height: '95%',
    },
    fullWidth: {
        height: '85%',
    },
    servLinesView: {
        width: '85%',
        height: '100%',
    },
    functionalityButtons: {
        justifyContent: 'flex-end'
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
});
//#endregion

export default HRAdminCategoriesScreen;